import type { BorderSide } from '../model/form.types';

export type BorderSideName = 'top' | 'right' | 'bottom' | 'left';

export const SIDES = ['top', 'right', 'bottom', 'left'] as const satisfies readonly BorderSideName[];

export const RULE_HIGHLIGHT_WIDTH = 2;
export const RULE_HIGHLIGHT_GAP = 1;

export function isBorderSideActive(side: BorderSide | undefined): boolean {
  return !!side && (side.width ?? 0) > 0 && side.style !== 'none';
}

export function outerBorderHalf(side: BorderSideName, width: number): number {
  return side === 'left' || side === 'top' ? Math.ceil(width / 2) : Math.floor(width / 2);
}

/**
 * How far a custom cell border reaches into the padding box. The border straddles the grid line, so
 * only the half that is not pulled outward by the overlay covers cell content.
 */
export function customBorderBleed(side: BorderSideName, data: BorderSide | undefined): number {
  if (!isBorderSideActive(data)) return 0;

  const width = data!.width ?? 0;
  return width - outerBorderHalf(side, width);
}

/** Where the applied-rule ring starts, measured from the padding box. */
export function ruleHighlightInset(side: BorderSideName, data: BorderSide | undefined): number {
  if (!isBorderSideActive(data)) return 0;
  return customBorderBleed(side, data) + RULE_HIGHLIGHT_GAP;
}

/**
 * Innermost pixel painted by the cell decorations, measured from the padding box. Anything anchored
 * to a cell corner has to clear this to avoid sitting on top of a border.
 */
export function cellDecorationDepth(
  side: BorderSideName,
  data: BorderSide | undefined,
  hasAppliedRule: boolean
): number {
  if (!hasAppliedRule) return customBorderBleed(side, data);
  return ruleHighlightInset(side, data) + RULE_HIGHLIGHT_WIDTH;
}
